import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "../Psicopedagogico/moduloPiscologiaIngreso.css";
import ObtieneDatosCookies from "../obtieneDatosCookiesUsuario";

const API = "https://wwwa.isepsantafe.net.ar/expedientes/ingresos";

function ModuloPiscologiaIngreso({ datos, refres, etapa }) {
  const Usuario = ObtieneDatosCookies();

  const [selected, setSelected] = useState(null);
  const [nuevaObservacion, setNuevaObservacion] = useState("");
  const [observacionesAnteriores, setObservacionesAnteriores] = useState("");
  const [personaSeleccionada, setPersonaSeleccionada] = useState(null);
  const [evaluacionExistente, setEvaluacionExistente] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // =========================================================
  // ID REAL DEL ITEM
  // =========================================================
  const itemIdFinal = useMemo(() => {
    return (
      datos?.item?.proceso_etapa_item_id ||
      datos?.item?.item_id ||
      datos?.item?.idItem ||
      datos?.item?.id ||
      datos?.item
    );
  }, [datos]);

  // =========================================================
  // PERSONA
  // =========================================================
  useEffect(() => {
    setPersonaSeleccionada(datos?.persona || null);
  }, [datos]);

  // =========================================================
  // CONSULTAR EVALUACIÓN EXISTENTE
  // =========================================================
  useEffect(() => {
    let mounted = true;

    const fetchEvaluacion = async () => {
      if (
        !datos?.persona?.dni ||
        !itemIdFinal ||
        typeof itemIdFinal === "object"
      ) {
        return;
      }

      setCargando(true);

      try {
        const res = await axios.post(
          `${API}/getEvaluacionItem`,
          {
            dni: datos.persona.dni,
            item_id: Number(itemIdFinal),
          },
          {
            withCredentials: true,
          }
        );

        if (!mounted) return;

        const evaluacion = res.data?.evaluacion || null;

        if (evaluacion) {
          const estado = String(
            evaluacion.estado || ""
          )
            .toLowerCase()
            .trim();

          const resultado = String(
            evaluacion.resultado || ""
          )
            .toLowerCase()
            .trim()
            .replaceAll("_", " ");

          // ---------------------------------------------
          // PRESELECCIONAR RESULTADO ACTUAL
          // ---------------------------------------------
          if (
            estado === "aprobado" ||
            resultado === "apto"
          ) {
            setSelected("apto");
          } else if (
            estado === "rechazado" ||
            resultado === "no apto" ||
            resultado === "noapto"
          ) {
            setSelected("no_apto");
          } else {
            setSelected(null);
          }

          setObservacionesAnteriores(
            evaluacion.observaciones || ""
          );

          setEvaluacionExistente(true);
        } else {
          setSelected(null);
          setObservacionesAnteriores("");
          setEvaluacionExistente(false);
        }

        setNuevaObservacion("");
      } catch (error) {
        console.error(
          "Error al consultar evaluación:",
          error
        );

        if (mounted) {
          Swal.fire(
            "Error",
            error.response?.data?.message ||
              error.response?.data?.error ||
              "No se pudo consultar la evaluación anterior",
            "error"
          );
        }
      } finally {
        if (mounted) {
          setCargando(false);
        }
      }
    };

    fetchEvaluacion();

    return () => {
      mounted = false;
    };
  }, [
    datos?.persona?.dni,
    itemIdFinal,
  ]);

  // =========================================================
  // FECHA ARGENTINA
  // =========================================================
  const obtenerFechaActual = () => {
    return new Intl.DateTimeFormat(
      "es-AR",
      {
        timeZone:
          "America/Argentina/Buenos_Aires",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }
    ).format(new Date());
  };

  // =========================================================
  // GUARDAR / MODIFICAR EVALUACIÓN
  // =========================================================
  const handleGuardar = async () => {
    if (!personaSeleccionada) {
      Swal.fire(
        "Error",
        "No hay persona cargada",
        "error"
      );
      return;
    }

    if (
      !itemIdFinal ||
      typeof itemIdFinal === "object"
    ) {
      Swal.fire(
        "Error",
        "El item_id no es válido",
        "error"
      );

      console.error(
        "ITEM INVÁLIDO:",
        datos?.item
      );

      return;
    }

    if (!selected) {
      Swal.fire(
        "Atención",
        "Seleccioná APTO o NO APTO",
        "warning"
      );
      return;
    }

    // =====================================================
    // DATOS DEL EVALUADOR
    // =====================================================
    const evaluador = Usuario
      ? `${
          Usuario.identificacion || ""
        } ${
          Usuario.apellido || ""
        }, ${
          Usuario.nombre || ""
        } (SEDE: ${
          Usuario.sedeID || ""
        })`
      : "sistema";

    const resultadoTexto =
      selected === "apto"
        ? "APTO"
        : "NO APTO";

    // =====================================================
    // NUEVA ENTRADA PARA EL HISTORIAL
    // =====================================================
    const nuevaEntrada = [
      "========================================",
      `Fecha: ${obtenerFechaActual()}`,
      `Resultado: ${resultadoTexto}`,
      `Nueva observación: ${
        nuevaObservacion.trim() ||
        "Sin nueva observación"
      }`,
      `Evaluador: ${evaluador}`,
    ].join("\n");

    const observacionesCompletas =
      observacionesAnteriores.trim()
        ? `${observacionesAnteriores.trim()}\n\n${nuevaEntrada}`
        : nuevaEntrada;

    // =====================================================
    // PAYLOAD
    // =====================================================
    const payload = {
      persona_id:
        personaSeleccionada.dni ||
        personaSeleccionada.id,

      dni:
        personaSeleccionada.dni ||
        null,

      item_id:
        Number(itemIdFinal),

      estado:
        selected === "apto"
          ? "aprobado"
          : "rechazado",

      tipo: "evaluacion",

      resultado:
        selected,

      observaciones:
        observacionesCompletas,

      proceso_id:
        etapa?.proceso_id ||
        etapa?.etapa_proceso_id ||
        datos?.proceso_id ||
        datos?.item?.proceso_id ||
        1,

      proceso_etapa_id:
        etapa?.id ||
        datos?.proceso_etapa_id ||
        null,

      proceso_etapa_instancia_id:
        datos?.item
          ?.proceso_etapa_instancia_id ||
        datos?.instancia ||
        datos?.instancia_id ||
        null,
    };

    // =====================================================
    // CONFIRMACIÓN
    // =====================================================
    const result = await Swal.fire({
      title: evaluacionExistente
        ? "¿Actualizar evaluación?"
        : "¿Confirmar evaluación?",

      html: `
        <div style="text-align:left">

          <b>Persona:</b>
          ${personaSeleccionada.apellido || ""},
          ${personaSeleccionada.nombre || ""}
          (${personaSeleccionada.dni || ""})

          <br/><br/>

          <b>Resultado:</b>
          ${resultadoTexto}

          <br/><br/>

          <b>Nueva observación:</b>
          <br/>

          ${
            nuevaObservacion.trim() ||
            "Sin nueva observación"
          }

          ${
            evaluacionExistente
              ? `
                <br/><br/>

                <div
                  style="
                    background:#fef3c7;
                    border:1px solid #f59e0b;
                    border-radius:8px;
                    padding:10px;
                    color:#92400e;
                  "
                >
                  <b>
                    Esta evaluación ya fue realizada.
                  </b>

                  <br/>

                  El resultado actual será modificado
                  y se conservará el historial anterior.
                </div>
              `
              : ""
          }

        </div>
      `,

      icon: "warning",

      showCancelButton: true,

      confirmButtonText:
        evaluacionExistente
          ? "Actualizar"
          : "Guardar",

      cancelButtonText:
        "Cancelar",
    });

    if (!result.isConfirmed) {
      return;
    }

    setGuardando(true);

    // =====================================================
    // GUARDAR
    // =====================================================
    try {
      const res = await axios.post(
        `${API}/togglePersonaItem`,
        payload,
        {
          withCredentials: true,
        }
      );

      console.log(
        "RESPUESTA EVALUACIÓN PSICOLOGÍA:",
        res.data
      );

      // Actualizamos localmente el historial
      setObservacionesAnteriores(
        observacionesCompletas
      );

      setNuevaObservacion("");

      setEvaluacionExistente(true);

      // Refrescar listado principal
      if (typeof refres === "function") {
        await refres();
      }

      await Swal.fire(
        "Guardado",
        evaluacionExistente
          ? "La evaluación fue actualizada correctamente"
          : "La evaluación fue registrada correctamente",
        "success"
      );
    } catch (error) {
      console.error(
        "Error al guardar evaluación:",
        error
      );

      Swal.fire(
        "Error",
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "No se pudo guardar la evaluación",
        "error"
      );
    } finally {
      setGuardando(false);
    }
  };

  // =========================================================
  // SIN DATOS
  // =========================================================
  if (!datos || !datos.persona) {
    return (
      <div className="psico-cargando">
        Cargando datos...
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <div className="psico-card">

      {/* TITULO */}
      <h1 className="psico-titulo">
        EVALUACIÓN {etapa?.nombre || ""}
      </h1>

      {/* AVISO DE EVALUACIÓN EXISTENTE */}
      {evaluacionExistente && (
        <div
          style={{
            background: "#fef3c7",
            color: "#92400e",
            border:
              "1px solid #f59e0b",
            padding: 10,
            borderRadius: 8,
            textAlign: "center",
            fontWeight: "bold",
          }}
        >
          Esta persona ya posee una
          evaluación. Puede modificar el
          resultado y agregar una nueva
          observación todas las veces que
          sea necesario.
        </div>
      )}

      {/* PERSONA */}
      <div className="psico-info">
        <strong>Evaluando a:</strong>{" "}
        {personaSeleccionada?.apellido ||
          ""}
        ,{" "}
        {personaSeleccionada?.nombre ||
          ""}{" "}
        (
        {personaSeleccionada?.dni ||
          ""}
        )
      </div>

      {cargando ? (
        <div className="psico-cargando">
          Cargando evaluación...
        </div>
      ) : (
        <>

          {/* =========================================
              APTO / NO APTO
              ========================================= */}
          <div className="psico-grupo">

            <button
              type="button"
              disabled={guardando}
              className={`psico-btn psico-apto ${
                selected === "apto"
                  ? "psico-active"
                  : ""
              }`}
              onClick={() =>
                setSelected("apto")
              }
            >
              APTO
            </button>

            <button
              type="button"
              disabled={guardando}
              className={`psico-btn psico-noapto ${
                selected === "no_apto"
                  ? "psico-active"
                  : ""
              }`}
              onClick={() =>
                setSelected("no_apto")
              }
            >
              NO APTO
            </button>

          </div>

          {/* =========================================
              HISTORIAL
              ========================================= */}
          {observacionesAnteriores && (
            <>
              <label className="psico-label">
                Historial de observaciones
              </label>

              <textarea
                className="psico-historial"
                readOnly
                value={
                  observacionesAnteriores
                }
              />
            </>
          )}

          {/* =========================================
              NUEVA OBSERVACIÓN
              ========================================= */}
          <label className="psico-label">
            Nueva observación
          </label>

          <textarea
            className="psico-textarea"
            value={nuevaObservacion}
            disabled={guardando}
            onChange={(e) =>
              setNuevaObservacion(
                e.target.value
              )
            }
            placeholder="Ingrese una nueva observación..."
          />

          {/* =========================================
              GUARDAR
              ========================================= */}
          <div className="psico-guardar" style={{ textAlign: "center" }}>

            <button
              type="button"
              disabled={
                guardando ||
                cargando ||
                !selected
              }
              onClick={handleGuardar}
              className="psico-btn-guardar"
            >
              {guardando
                ? "Guardando..."
                : evaluacionExistente
                ? "MODIFICAR EVALUACIÓN"
                : "GUARDAR EVALUACIÓN"}
            </button>

          </div>

        </>
      )}

    </div>
  );
}

export default ModuloPiscologiaIngreso;